import React, { useEffect, useState } from 'react';
import { Building } from '../../../confi-state';
import { Segment } from '../models/segment';
import { ColumnService } from './column-service';
import { columnDefs, subtypeEnum, variantEnum } from './models/column-definition';
import { columnType } from './models/column-type.enum';
import { RowhouseColumn, PropsRowhouse } from './models/props-config';
import { segmentType } from './models/segment-type';
import { partTypeEnum } from './models/part-type.enum';
import Refine from '../Refine/Refine';
import { Part } from '../models/part';
import { RefineService } from '../Refine/refine.service';
import { ServiceManager } from '../../../services/service-manager';
import { buildingType } from '../models/building-type.enum';
import { SectionJSON } from '../../../models/section-json';

const buildingChange = (buildingProps: PropsRowhouse, currentBuilding: Building, onChangeBuilding) => {
    onChangeBuilding(currentBuilding.projectId, currentBuilding.siteId, { ...currentBuilding, props: { ...buildingProps } }, true);
};

const RowhouseRefined = ({ currentBuilding, onChangeBuilding, sectionJson }: { currentBuilding: Building, onChangeBuilding: (projectId: number | string, siteId: number | string, building: Building, saveToDb: boolean) => void, sectionJson: SectionJSON }) => {

    const [buildingProps, setBuildingProps] = useState<PropsRowhouse>(
        undefined
    );

    const getColumnTypes = () => {
        let cols = columnDefs;
        return cols.map(c => c.type);
    }

    const setUpDefaultColumnFromDefinition = (ct: columnType) => {
        const coldef = columnDefs.find(c => c.type === ct)
        const columnService = new ColumnService();
        const segments = RefineService.getSegmentTypes(ct, columnDefs);
        if (segments && segments.length > 0) {
            let seg: Segment<segmentType, subtypeEnum, variantEnum>[] = []
            let amountOfUnits = [];
            segments.forEach(s => {
                const sg = RefineService.getSegmentDefinitionsForType(ct, s, columnDefs);
                const segDef = sg[0];
                let st = segDef.subtype;

                const floorDef = segDef.floorVariants[0];
                const floors = floorDef.floors;

                let variantDef = floorDef.variants[0];

                seg.push({
                    type: s as segmentType,
                    subtype: st,
                    sectionTypes: variantDef.sections,
                    floorsInSegment: floors
                })
            })
            const col: RowhouseColumn = {
                id: Math.random().toString(),
                type: ct,
                bays: coldef.bays,
                segments: seg,
                amountOfUnits
            }

            return col;
        }
    }

    const getDefaultColumnTypeForPart = (part) => {
        let type;
        if (part.type === partTypeEnum.body) {
            type = columnType.H_S;
        }
        return type;
    }

    useEffect(() => {
        if (currentBuilding && currentBuilding.props) {
            const buildingProps = currentBuilding.props as PropsRowhouse;
            if (buildingProps && (!buildingProps.partsRefined || buildingProps.partsRefined.length === 0)) {
                const bp: any = { ...buildingProps, parts: [...buildingProps.parts] }
                // currently this is a single entity, but it might be expanded in the future
                bp.partsRefined = [{
                    id: 'first',
                    parts: ServiceManager.buildingHelperService.cloneParts(bp.parts)
                }];
                setBuildingProps({ ...bp });
                buildingChange({ ...bp }, currentBuilding, onChangeBuilding)
            } else {
                setBuildingProps(buildingProps);
            }
        }
        //eslint-disable-next-line
    }, [currentBuilding]);

    const getPartName = (p: partTypeEnum) => {
        let name = '';
        switch (p) {
            case partTypeEnum.body:
                name = 'Body'
                break;
        }
        return name;
    }

    const getVariantName = (variant: variantEnum): string => {
        return '';
    }

    const updateBuildingColumn = (part, column) => {
        const bp = { ...buildingProps, partsRefined: [...buildingProps.partsRefined] };

        const partTypeToAdd = part.type;
        const partToAdd = bp.partsRefined[0].parts.find(p => p.type === partTypeToAdd);
        let columns = partToAdd.columns;
        columns = columns.map(col => col.id === column.id ? column : col);
        partToAdd.columns = columns;

        bp.partsRefined[0].parts = ServiceManager.babylonService.rowhouseBabylonService.mapPartsTo3D(
            bp.partsRefined[0].parts,
            true,
        );

        bp.info = ServiceManager.buildingHelperService.computePartInfo(bp.partsRefined[0].parts as any);

        const columnService = new ColumnService();
        columnService.computeAmountsForParts(bp.partsRefined[0].parts);
        bp.totalAmountOfAllUnits = columnService.getTotalAmountOfAllUnits(bp.partsRefined[0].parts);

        buildingChange(bp, currentBuilding, onChangeBuilding);
    }

    const updateBuildingColumns = (part, columns) => {
        const bp = { ...buildingProps, partsRefined: [...buildingProps.partsRefined] };

        const partToAdd = part.type;
        const partAdd = bp.partsRefined[0].parts.find(p => p.type === partToAdd);
        partAdd.columns = columns;

        bp.partsRefined[0].parts = ServiceManager.babylonService.rowhouseBabylonService.mapPartsTo3D(
            bp.partsRefined[0].parts,
            true,
        );

        bp.info = ServiceManager.buildingHelperService.computePartInfo(bp.partsRefined[0].parts as any);

        const columnService = new ColumnService();
        columnService.computeAmountsForParts(bp.partsRefined[0].parts);
        bp.totalAmountOfAllUnits = columnService.getTotalAmountOfAllUnits(bp.partsRefined[0].parts);

        buildingChange(bp, currentBuilding, onChangeBuilding);
    }

    const getSegmentName = (p: segmentType) => {
        let name = '';
        switch (p) {
            case segmentType.middle:
                name = 'Base';
                break;
        }
        return name;
    }

    const getSegmentSubtypeName = (p: subtypeEnum) => {
        let name = '';
        switch (p) {
            case subtypeEnum.single:
                name = 'Geen'
                break;
        }
        return name;
    }

    return (
        <div className="group" data-testid="RowhouseRefined">
            {buildingProps && buildingProps.partsRefined &&
                <>
                    <div>
                        <h2 className="greyed-out-text">Gebouw verfijnen</h2>
                        <Refine
                            part={buildingProps.partsRefined[0].parts[0]}
                            bt={currentBuilding.type}
                            buildingId={currentBuilding.id as string}
                            sectionJson={sectionJson}
                            segmentTypesMultipleFloors={[]}
                            segmentTypesAllowCustom={[]}
                            columnDefs={columnDefs}
                            floorHeight={ServiceManager.buildingHelperService.floorHeightOfBuilding(buildingType.rowhouse)}
                            updateBuildingColumn={updateBuildingColumn}
                            updateBuildingColumns={updateBuildingColumns}
                            getDefaultColumnTypeForPart={getDefaultColumnTypeForPart}
                            setUpDefaultColumnFromDefinition={setUpDefaultColumnFromDefinition}
                            getVariantName={getVariantName as any}
                            getSegmentName={getSegmentName as any}
                            getSegmentSubtypeName={getSegmentSubtypeName as any}
                        ></Refine>
                    </div>
                </>
            }
        </div>
    )
};

export default RowhouseRefined;
